/**
 * Utilities for extracting text lines from a QuestionnaireResponse and
 * generating a diff-based changes summary for addendum auto-generation.
 */

import type {
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { getItemAnswerText, findQuestionnaireItemText } from '../fhirNoteMapping';
import { stripHtml } from '../utils/stripHtml';
import type { AddendumLineChange } from './types';

/**
 * Walk a QR's items and flatten to one string per question/answer (leaf item).
 * Multi-line answers are normalized to a single line so one question = one entry.
 */
export function extractQRTextLines(
  qr: QuestionnaireResponse,
  questionnaire?: Questionnaire | null,
): string[] {
  const lines: string[] = [];

  function walk(items: QuestionnaireResponseItem[]) {
    for (const item of items) {
      const title =
        item.text ??
        (questionnaire && item.linkId
          ? findQuestionnaireItemText(questionnaire, item.linkId)
          : undefined) ??
        '';

      if (item.item && item.item.length > 0) {
        walk(item.item);
      } else {
        const answer = stripHtml(getItemAnswerText(item));
        const singleLineAnswer = answer ? answer.replace(/\n/g, ' ').trim() : '';
        if (singleLineAnswer) {
          lines.push(title ? `${title}: ${singleLineAnswer}` : singleLineAnswer);
        } else if (title) {
          lines.push(`${title}: —`);
        }
      }
    }
  }

  walk(qr.item ?? []);
  return lines;
}

/**
 * Per-index diff: pairs removed/added at the same index so the UI can show
 * complete before → after like Google Docs.
 */
export function computeAddendumLineChanges(
  beforeLines: string[],
  afterLines: string[],
): AddendumLineChange[] {
  const out: AddendumLineChange[] = [];
  const max = Math.max(beforeLines.length, afterLines.length);
  for (let i = 0; i < max; i++) {
    const b = beforeLines[i];
    const a = afterLines[i];
    if (b === a) continue;
    const removed = b !== undefined && b !== a ? b : undefined;
    const added = a !== undefined && a !== b ? a : undefined;
    if (removed || added) {
      out.push({ removed, added });
    }
  }
  return out;
}
